import classnames from 'classnames';
import React from 'react';
import {TableInstance} from 'react-table';

import cs from './ContractHistory.module.scss';
import ShowMoreRows from './ShowMoreRows';
import {FIRST_ROWS, ROWS_PER_PAGE} from './utils';

/**
 * A billing table that opens on its first few rows and grows a page at a time. With `loadMore`, it
 * fetches the next page from the server once every loaded row is showing.
 */
function PagedTable<T extends object>({
  instance: {getTableProps, getTableBodyProps, headerGroups, rows, prepareRow},
  numericColumns,
  hasUnloadedRows = false,
  loadMore,
  loadingMore,
}: {
  instance: TableInstance<T>;
  numericColumns: ReadonlySet<string>;
  hasUnloadedRows?: boolean;
  loadMore?: () => void;
  loadingMore?: boolean;
}) {
  const [shownRowCount, setShownRowCount] = React.useState(FIRST_ROWS);
  const nextShownRowCount =
    shownRowCount < ROWS_PER_PAGE ? ROWS_PER_PAGE : shownRowCount + ROWS_PER_PAGE;

  return (
    <div>
      <div className={cs.tableWrapper}>
        <table {...getTableProps()} className={cs.table}>
          <thead>
            {headerGroups.map((headerGroup, i) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                {headerGroup.headers.map((column, i) => (
                  <th
                    {...column.getHeaderProps()}
                    className={classnames({[cs.numeric]: numericColumns.has(column.id)})}
                    key={i}
                  >
                    <div>{column.render('Header')}</div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {rows.slice(0, shownRowCount).map((row, i) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={i}>
                  {row.cells.map((cell, i) => (
                    <td
                      {...cell.getCellProps()}
                      className={classnames({[cs.numeric]: numericColumns.has(cell.column.id)})}
                      key={i}
                    >
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ShowMoreRows
        shownRowCount={shownRowCount}
        nextShownRowCount={nextShownRowCount}
        loadedRowCount={rows.length}
        hasUnloadedRows={hasUnloadedRows}
        loading={loadingMore}
        onShowMore={() => {
          if (nextShownRowCount > rows.length && hasUnloadedRows) {
            loadMore?.();
          }
          setShownRowCount(nextShownRowCount);
        }}
        onShowLess={() => setShownRowCount(FIRST_ROWS)}
      />
    </div>
  );
}

export default PagedTable;
